import { Field, Form, Formik } from "formik";
import TextField from "pages/_components/fields/TextField";
import GridTable from "pages/_components/GridTable/GridTable";
import { ResponsiveHeader } from "pages/_components/header";
import MainContainer from "pages/_components/MainContainer";
import Notification from "pages/_components/Notification";
import React, { useEffect, useState } from "react";
import * as i18n from "util/i18n";
import * as Yup from "yup";
import Selector from "pages/_components/fields/formik/Selector";
import GeneralCredentialForm from "pages/_components/Forms/GeneralCredentialForm";
import { selectors as comexSelectors, actions as comexActions } from "reducers/comex";
import CustomText from "pages/_components/CustomText/CustomText";
import Drawer from "pages/_components/Drawer/Drawer";
import Checkbox from "pages/_components/Checkbox";
import { bool } from "prop-types";
import { connect, useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import ComexStepper from "../_components/ComexStepper";

const FORM_ID = "comex.contact";
const MAX_REQUIRED = "34";
const MAX_REQUIRED_ADDRESS = "40";
const MAX_REQUIRED_COUNTRY_CODE = "10";

const ModifyComexContactPage = ({ isDesktop }) => {
    const dispatch = useDispatch();

    const { comexTransfer = {}, comexContact = {}, comexCurrencies = [], comexCountries = [], fetching } = useSelector(
        (state) => state.comex,
    );

    const { id } = useParams();

    const [showAccountNumberInfo, setShowAccountNumberInfo] = useState(false);
    const [showDestinationInfo, setShowDestinationInfo] = useState(false);
    const [isModified, setIsModified] = useState(false);
    const [selectedBankType, setSelectedBankType] = useState(comexContact?.destination);

    useEffect(() => {
        dispatch(comexActions.getComexSelectors());
    }, []);

    const handleCheckboxChange = (e, setFieldValue, otherField) => {
        const targetCode = otherField == "aba" ? "swift" : "aba";
        setSelectedBankType(targetCode);
        if (!e) setSelectedBankType(null);
        setFieldValue(otherField, false);
    };

    useEffect(() => {
        if (comexContact?.destination)
            setSelectedBankType(comexContact?.destination);
    }, [comexContact?.destination]);

    useEffect(() => {
        if (id) {
            dispatch(comexActions.modifyComexContactPre(id));
        }
    }, [id]);

    const initialValues = {
        idContactDestination: id,
        recipientName: comexContact?.recipientName || "",
        iban: comexContact?.iban || "",
        swift: comexContact?.destination === "SWIFT",
        aba: comexContact?.destination === "ABA",
        country: comexContact?.country || "",
        number: comexContact?.number || "",
        currency: comexContact?.currency || "",
        address: comexContact?.address || "",
        countryCode: comexContact?.countryCode || "",
    };

    const newContactSchema = Yup.object()
        .shape({
            recipientName: Yup.string().required(i18n.get(`global.error.required`)),

            iban: Yup.string()
                .max(parseInt(MAX_REQUIRED, 10), `${i18n.get("forms.validation.max.required")} ${MAX_REQUIRED}`)
                .required(i18n.get(`global.error.required`))
                .matches(/^[a-zA-Z0-9]{1,34}$/, i18n.get("global.incorrect.format")),

            swift: Yup.boolean(),
            aba: Yup.boolean(),

            address: Yup.string()
                .max(
                    parseInt(MAX_REQUIRED_ADDRESS, 10),
                    `${i18n.get("forms.validation.max.required")} ${MAX_REQUIRED_ADDRESS}`,
                )
                .required(i18n.get(`global.error.required`)),

            country: Yup.string().required(i18n.get(`global.error.required`)),

            number: Yup.string()
                .required(i18n.get(`global.error.required`))
                .when(["swift", "aba"], {
                    is: (swift, aba) => swift,
                    then: Yup.string().matches(/^[a-zA-Z0-9]{8,11}$/, i18n.get("comex.forms.validation.invalid.swift")),
                })
                .when(["swift", "aba"], {
                    is: (swift, aba) => aba,
                    then: Yup.string().matches(/^\d{9}$/, i18n.get("comex.forms.validation.invalid.aba")),
                }),

            currency: Yup.string().required(i18n.get(`global.error.required`)),

            countryCode: Yup.string()
                .max(
                    parseInt(MAX_REQUIRED_COUNTRY_CODE, 10),
                    `${i18n.get("forms.validation.max.required")} ${MAX_REQUIRED_COUNTRY_CODE}`,
                )
                .required(i18n.get(`global.error.required`)),
        })
        .test("one-required", i18n.get("forms.validation.at.least.one.required"), (values) => {
            const { swift, aba } = values;
            return swift || aba;
        })
        .test("only-one-checked", i18n.get("forms.validation.only.one.checkbox"), (values) => {
            const { swift, aba } = values;
            return !(swift && aba);
        });

    const [data, setData] = useState(initialValues);

    useEffect(() => {
        const hasChanged = Object.keys(initialValues).some((key) => data[key] !== initialValues[key]);
        setIsModified(hasChanged);
    }, [data, initialValues]);

    useEffect(() => {
        let countryName = "";
        if (data.country) {
            const selectedCountry = comexCountries.find((c) => c.id === data.country);
            countryName = selectedCountry?.description || "";
        }

        dispatch(
            comexActions.setComexTransfer({
                contactDestination: {
                    ...data,
                    countryName,
                },
            }),
        );
    }, [data]);

    const DestinationInfo = () => (
        <Drawer
            className="help-slider"
            openSidebar={showDestinationInfo}
            toggleSidebar={() => setShowDestinationInfo(false)}>
            <div className="container">
                <div
                    className="row"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: i18n.get("comex.destination.info.content"),
                    }}
                />
            </div>
        </Drawer>
    );

    const AccountNumberInfo = () => (
        <Drawer
            className="help-slider"
            openSidebar={showAccountNumberInfo}
            toggleSidebar={() => setShowAccountNumberInfo(false)}>
            <div className="container">
                <div
                    className="row"
                    // eslint-disable-next-line react/no-danger
                    dangerouslySetInnerHTML={{
                        __html: i18n.get("comex.iban.info.content"),
                    }}
                />
            </div>
        </Drawer>
    );

    return (
        <>
            <Notification scopeToShow="CreateComexContact" />
            <DestinationInfo />
            <AccountNumberInfo />
            <ResponsiveHeader id="comex.modify.contactDestination.title" />

            <MainContainer showLoader={fetching}>
                {!isDesktop && <ComexStepper isDesktop={isDesktop} currentStep={0} />}

                <Formik
                    initialValues={initialValues}
                    onSubmit={(values) => setData(values)}
                    validationSchema={newContactSchema}>
                    {({ values, handleChange, setFieldValue, isValid, dirty }) => {
                        setData(values);
                        return (
                            <>
                                <Form onChange={handleChange}>
                                    <GridTable className="form">
                                        <GridTable.Container columns={12} rows={2} columnsTemplate="fixed">
                                            <GridTable.Data columnStart={2} columnWidth={5} rowStart={1}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="recipientName"
                                                    hidePlaceholder
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={7} columnWidth={5} rowStart={1}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="iban"
                                                    hidePlaceholder
                                                    showTooltip
                                                    onTooltipClick={() => setShowAccountNumberInfo(true)}
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data
                                                flexDirection="column"
                                                columnStart={2}
                                                columnWidth={5}
                                                rowStart={2}>
                                                <CustomText
                                                    className="data-label"
                                                    text={`${FORM_ID}.destination.label`}
                                                    textType="I18N"
                                                    showTooltip
                                                    onTooltipClick={() => setShowDestinationInfo(true)}
                                                />
                                                <div className="flex gap-6">
                                                    <Field
                                                        component={Checkbox}
                                                        idForm={FORM_ID}
                                                        name="swift"
                                                        labelTextProps={{ defaultValue: "SWIFT" }}
                                                        style={{ marginRight: "16px" }}
                                                        onChange={(e) => handleCheckboxChange(e, setFieldValue, "aba")}
                                                    />

                                                    <Field
                                                        component={Checkbox}
                                                        idForm={FORM_ID}
                                                        name="aba"
                                                        labelTextProps={{ defaultValue: "ABA" }}
                                                        onChange={(e) => handleCheckboxChange(e, setFieldValue, "swift")}
                                                    />
                                                </div>
                                            </GridTable.Data>

                                            <GridTable.Data columnStart={7} columnWidth={5} rowStart={2}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="address"
                                                    hidePlaceholder
                                                />
                                            </GridTable.Data>

                                            <GridTable.Data columnStart={2} columnWidth={5} rowStart={3}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="number"
                                                    hidePlaceholder
                                                    labelText={selectedBankType != null ? `${FORM_ID}.bankType.${selectedBankType?.toLowerCase()}.label` : `${FORM_ID}.number.label`}
                                                />
                                            </GridTable.Data>

                                            <GridTable.Data columnStart={7} columnWidth={5} rowStart={3}>
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="country"
                                                    options={comexCountries.map(({ id, description }) => ({
                                                        value: id,
                                                        label: description,
                                                    }))}
                                                    placeholder=" "
                                                    value={null}
                                                />
                                            </GridTable.Data>

                                            <GridTable.Data columnStart={2} columnWidth={5} rowStart={4}>
                                                <Field
                                                    component={Selector}
                                                    idForm={FORM_ID}
                                                    name="currency"
                                                    options={comexCurrencies.map(({ id, description }) => ({
                                                        value: id,
                                                        label: description,
                                                    }))}
                                                    placeholder=" "
                                                    value={null}
                                                />
                                            </GridTable.Data>

                                            <GridTable.Data columnStart={7} columnWidth={5} rowStart={4}>
                                                <Field
                                                    component={TextField}
                                                    idForm={FORM_ID}
                                                    name="countryCode"
                                                    hidePlaceholder
                                                />
                                            </GridTable.Data>
                                        </GridTable.Container>
                                    </GridTable>
                                </Form>
                                <GeneralCredentialForm
                                    transactionData={comexTransfer?.contactDestination}
                                    customSubmitLabel="comex.contact.modify.submit"
                                    action={comexActions.modifyComexContact}
                                    idActivity="modify.comex.contact"
                                    hideIndications
                                    isDisabled={!isValid || !dirty || !isModified}
                                />
                            </>
                        );
                    }}
                </Formik>
            </MainContainer>
        </>
    );
};
ModifyComexContactPage.propTypes = {
    isDesktop: bool.isRequired,
};

const mapStateToProps = (state) => ({
    fetching: comexSelectors.getFetching(state),
});

export default connect(mapStateToProps)(ModifyComexContactPage);
